import i18n from '@/utils/i18n';
import {
  AutoPaySettings_GetNextAutoPayInfoQuery,
  AutoPaySettings_GetNextAutoPayInfoQueryVariables,
} from '@/types';
import { formatDate } from '@/utils/date';
import { GET_NEXT_AP_INFO_QUERY } from '@/contexts/payments-context';
import { NOT_FOUND_CODE } from '@/utils/apollo-client';
import { PaymentCarouselOptionType } from '@/components/payments/payment-banner';
import { useGetPaymentPlanNextRun } from './use-get-payment-plan-next-run';
import { useMemo } from 'react';
import { useWrappedQuery } from './use-wrapped-query';

export const useGetPaymentBannersData = (
  restaurantUUID: string,
  supplierUUID: string,
) => {
  const { data: apData, loading: apLoading } = useWrappedQuery<
    AutoPaySettings_GetNextAutoPayInfoQuery,
    AutoPaySettings_GetNextAutoPayInfoQueryVariables
  >(GET_NEXT_AP_INFO_QUERY, {
    context: {
      ignoreGraphQLErrorCodes: [NOT_FOUND_CODE],
    },
    fetchPolicy: 'network-only',
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const { data: ppData, loading: ppLoading } = useGetPaymentPlanNextRun(
    restaurantUUID,
    supplierUUID,
  );

  const upcomingPP = useMemo(() => {
    if (!ppData) {
      return null;
    }
    const sortedDates = [...ppData.getPaymentPlanNextRunDate].sort((a, b) => {
      return (
        new Date(a.next_run_date).getTime() -
        new Date(b.next_run_date).getTime()
      );
    });
    if (sortedDates.length === 0) {
      return null;
    }
    return sortedDates[0];
  }, [ppData]);

  const data = useMemo(() => {
    const dataList = [];
    if (apData) {
      dataList.push({
        amount: apData?.getNextAutopayInfo.amount_in_micros!,
        date: new Date(apData?.getNextAutopayInfo.date!),
        text: i18n.t('autopay.next_autopay_on', {
          date: formatDate(new Date(apData?.getNextAutopayInfo.date!)),
        }),
        type: PaymentCarouselOptionType.NextAutopay,
      });
    }
    if (upcomingPP) {
      dataList.push({
        amount: upcomingPP.payment_plan?.installment_amount_in_micros!,
        date: new Date(upcomingPP.next_run_date!),
        text: i18n.t('payment_plan.next_payment_on', {
          date: formatDate(new Date(upcomingPP.next_run_date!)),
        }),
        type: PaymentCarouselOptionType.NextPaymentPlan,
      });
    }
    return dataList.sort((a, b) => {
      return a.date.getTime() - b.date.getTime();
    });
  }, [apData, upcomingPP]);

  return useMemo(() => {
    return {
      data,
      loading: apLoading || ppLoading,
      upcomingPP,
    };
  }, [data, apLoading, ppLoading, upcomingPP]);
};
